import React, { useCallback, useEffect, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, ThumbsDown, Drag } from '@icon-park/react';
import { getQueryParams } from '@/utils/common';
import {
  CheckGroup, TextArea, Button, FormContainer,
} from '@byte-design/ui';
import Draggable from 'react-draggable';

const emptyThumbsDownForm = {
  issues: [],
  details: '',
};

function Survey() {
  const { t } = useTranslation();
  const docId = getQueryParams('doc_id');
  const state = useLocalStore(() => ({
    hasSubmit: false,
    showThumbsDownMenu: false,
    reason: '',
    info: '',
    needReason: false,
  }));
  const [thumbsDownForm, setThumbsDownForm] = useState(emptyThumbsDownForm);
  const [isValidForm, setIsValidForm] = useState(false);

  const onClickThumbsUp = useCallback(() => {
    window.parent.postMessage({ type: 'thumbs_up' }, '*');
    setSurveyRecord({ [docId as string]: { lastTime: new Date().getTime() } });
    state.hasSubmit = true;
  }, []);

  const onClickThumbsDown = useCallback(() => {
    state.showThumbsDownMenu = true;
  }, []);

  const setSurveyRecord = useCallback((obj) => {
    let origin = JSON.parse(window.localStorage.getItem('docSurveySubmitRecord') || '{}');
    origin = { ...origin, ...obj };
    window.localStorage.setItem('docSurveySubmitRecord', JSON.stringify(origin));
  }, []);

  const onCancelForm = () => {
    state.showThumbsDownMenu = false;
  };

  const onSubmitForm = () => {
    window.parent.postMessage({ type: 'thumbs_down', ...thumbsDownForm }, '*');
    setSurveyRecord({ [docId as string]: { lastTime: new Date().getTime() } });
    state.hasSubmit = true;
  };

  const onChangeIssues = (vals: any) => {
    setThumbsDownForm((prev) => (
      { ...prev, issues: vals }
    ));
  };

  const onChangeDetails = (val: string) => {
    setThumbsDownForm((prev) => (
      { ...prev, details: val }
    ));
  };

  useEffect(() => {
    if (!docId) return;
    const origin = JSON.parse(window.localStorage.getItem('docSurveySubmitRecord') || '{}');
    // 存在当前文档记录 且在24小时内
    if (origin[docId] && (new Date().getTime() - (origin[docId]?.lastTime || new Date().getTime()) < 8600000)) {
      state.hasSubmit = true;
    } else {
      state.hasSubmit = false;
    }
  }, []);

  useEffect(() => {
    const isValid = thumbsDownForm.issues.length > 0 && thumbsDownForm.details.length > 0;
    setIsValidForm(isValid);
  }, [thumbsDownForm]);

  return (
    <Draggable axis="x" bounds={{ left: -300, right: 0 }} handle=".i-icon-drag">
      <div className={styles.container}>
        <Drag className={styles.handleIcon} />
        <div>
          {state.showThumbsDownMenu && !state.hasSubmit
            ? (
              <div className={styles.thumbsDownMenu}>
                <FormContainer
                  className={styles.issuesContainer}
                  vertical
                  label={t('doc_survey_issues')}
                  required
                >
                  <CheckGroup
                    className={styles.issuesCheckGroup}
                    value={thumbsDownForm.issues}
                    onValueChange={onChangeIssues}
                    type="checkbox"
                  >
                    <CheckGroup.Checkbox value="missing">
                      {t('doc_survey_item_1')}
                    </CheckGroup.Checkbox>
                    <CheckGroup.Checkbox value="incorrect">
                      {t('doc_survey_item_2')}
                    </CheckGroup.Checkbox>
                    <CheckGroup.Checkbox value="understand">
                      {t('doc_survey_item_3')}
                    </CheckGroup.Checkbox>
                    <CheckGroup.Checkbox value="find">
                      {t('doc_survey_item_4')}
                    </CheckGroup.Checkbox>
                    <CheckGroup.Checkbox value="broken">
                      {t('doc_survey_item_5')}
                    </CheckGroup.Checkbox>
                    <CheckGroup.Checkbox value="other">
                      {t('doc_survey_item_6')}
                    </CheckGroup.Checkbox>
                  </CheckGroup>
                </FormContainer>
                <FormContainer
                  className={styles.detailsContainer}
                  vertical
                  label={t('Details')}
                  required
                >
                  <TextArea
                    value={thumbsDownForm.details}
                    onValueChange={onChangeDetails}
                    placeholder={t('please_enter')}
                    maxLength={250}
                    rows={3}
                  />
                </FormContainer>
                <div className={styles.btns}>
                  <Button plain={false} size="sm" onClick={onCancelForm}>
                    {t('cancel')}
                  </Button>
                  <Button type="primary" size="sm" onClick={onSubmitForm} disabled={!isValidForm}>
                    {t('submit')}
                  </Button>
                </div>
              </div>
            )
            : (
              <>
                <div className={styles.title}>
                  {t('doc_solve_your_problem')}
                </div>
                <div className={styles.context}>
                  {state.hasSubmit
                    ? (
                      <div className={styles.notSolve}>
                        {t('doc_thank_feedback')}
                      </div>
                    )
                    : (
                      <>
                        <div className={styles.item} onClick={onClickThumbsUp}>
                          <ThumbsUp className={styles.icon} />
                          {t('solve')}
                        </div>
                        <div className={styles.item} onClick={onClickThumbsDown}>
                          <ThumbsDown className={styles.icon} />
                          {t('solve_not')}
                        </div>
                      </>
                    )}
                </div>
              </>
            )}
        </div>
      </div>
    </Draggable>
  );
}

export default React.memo(observer(Survey));
